'use client';

import { useId } from 'react';
import type { ChangeEventHandler, FormEventHandler, Ref } from 'react';
import { CalculatorActions } from 'components/Widget/shared/CalculatorActions';
import { COUNT_FIELD, FREQUENCY_LEGEND, FREQUENCY_OPTIONS } from './constants';
import type { FrequencyKey, FrequencyOption } from './types';

interface OverdraftCalculatorFormProps {
  /** Focus target after leaving the result. */
  ref?: Ref<HTMLFormElement>;
  count: number;
  frequency: FrequencyKey;
  onCountChange: (count: number) => void;
  onFrequencyChange: (option: FrequencyOption) => void;
  onCalculate: () => void;
}

export const OverdraftCalculatorForm = ({
  ref,
  count,
  frequency,
  onCountChange,
  onFrequencyChange,
  onCalculate,
}: OverdraftCalculatorFormProps) => {
  const countId = useId();
  // Keeps two calculators on one page out of each other's radio group.
  const frequencyName = useId();

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  const handleCountChange: ChangeEventHandler<HTMLInputElement> = event => onCountChange(Number(event.target.value));

  return (
    <form
      ref={ref}
      tabIndex={-1}
      aria-label="Overdraft fee calculator"
      className="flex flex-col gap-8"
      onSubmit={handleSubmit}>
      <div>
        <label htmlFor={countId} className="block text-body-saans">
          {COUNT_FIELD.label}
        </label>

        {/* The slider announces its own value and bounds, so this readout and the end labels are decoration. */}
        <p
          aria-hidden
          className="mx-auto mt-4 flex size-16 items-center justify-center rounded-lg bg-bg-primary text-heading-sm">
          {count}
        </p>

        <div className="mt-4 flex items-center gap-4">
          <span aria-hidden className="text-body-saans">
            {COUNT_FIELD.min}
          </span>
          <input
            id={countId}
            type="range"
            min={COUNT_FIELD.min}
            max={COUNT_FIELD.max}
            step={1}
            value={count}
            onChange={handleCountChange}
            className="w-full cursor-pointer accent-kale"
          />
          <span aria-hidden className="text-body-saans">
            {COUNT_FIELD.max}
          </span>
        </div>
      </div>

      <fieldset>
        <legend className="text-body-saans">{FREQUENCY_LEGEND}</legend>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row">
          {FREQUENCY_OPTIONS.map(option => (
            // The radio stays in the accessibility tree so the group keeps its arrow-key behavior.
            <label key={option.key} className="flex-1">
              <input
                type="radio"
                name={frequencyName}
                value={option.key}
                checked={frequency === option.key}
                onChange={() => onFrequencyChange(option)}
                className="peer sr-only"
              />
              {/* Hover styles sit here, not on the `peer`, which is too small to be hovered. */}
              <span className="block cursor-pointer rounded-lg border border-content-secondary bg-bg-primary px-6 py-4 text-center text-body-saans transition-shadow hover:ring-[6px] hover:ring-bg-tertiary peer-checked:border-kale peer-checked:bg-kale peer-checked:text-content-inverse peer-focus-visible:ring-[6px] peer-focus-visible:ring-bg-tertiary">
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <CalculatorActions className="sm:self-center" />
    </form>
  );
};
